import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';

import {
    Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  MenuItem,
  Select,
  Skeleton,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { PropsWithChildren, ReactElement, useState } from "react";
import { FormOption } from "~/utils/forms";
import UnavailableProviders from "./UnavailableProviders";
import { sortBy } from "lodash";
import { providerTraits } from '@caredna-marlin/utils/providerTraits';
import { SchedulerProvidersListQuery } from '~/graphql/__generated__/graphql';
import { useOrgConfig, VisitType } from '~/providers/OrgConfigProvider';

export type SchedulerFilterDialogInput = {
  daysOfWeek: string[];
  timeOfDay: string;
  providerGender: string;
  providerLanguage: string;
  provider: string[];
  useOverflowShifts: boolean;
};

type SchedulerFilterDialogProps = {
  providers: SchedulerProvidersListQuery["scheduleProvidersList"];
  onClose: () => void,
  values: SchedulerFilterDialogInput,
  visitType: VisitType,
  patient: { addressState?: string | null },
  setValues: (values: SchedulerFilterDialogInput) => void,
};

export const daysOfWeek = [
  { label: "S", value: "0" },
  { label: "M", value: "1" },
  { label: "T", value: "2" },
  { label: "W", value: "3" },
  { label: "Th", value: "4" },
  { label: "F", value: "5" },
  { label: "S", value: "6" },
];

const timesOfDay = [
  { label: "Morning", value: "morning" },
  { label: "Afternoon", value: "afternoon" },
];

const toggleButtons = (options: FormOption[]) => options.map((item) => (
  <ToggleButton color="secondary" key={item.value} value={item.value} sx={{pl: 2, pr: 2}}>
    {item.label}
  </ToggleButton>
))

export default function SchedulerFilterDialog({
  providers,
  visitType,
  patient,
  onClose,
  values,
  setValues,
}: SchedulerFilterDialogProps) {
  const [local, setLocal] = useState<SchedulerFilterDialogInput>(values);
  const { providerTypes } = useOrgConfig();

  const providersList = sortBy(providers.map((p) => {
    return {
      label: `${p.firstName} ${p.lastName}`,
      value: p.id.toString(),
      preferred: p.preferred,
      typeName: providerTypes[p.providerTypeId].name,
      invalidReason: providerInvalidReason(p, visitType, patient, local.useOverflowShifts),
    };
  }), 'label');

  return (
    <Dialog
      open={true}
      onClose={onClose}
      slotProps={{ paper: {
        component: "form",
        sx: { p: 2 },
      }}}
    >
      <DialogContent>
        <Grid container spacing={2}>
          <FilterGridRow label="Days of Week">
            <ToggleButtonGroup value={local.daysOfWeek} onChange={(_, val) => setLocal({ ...local, daysOfWeek: val })}>
              {toggleButtons(daysOfWeek)}
            </ToggleButtonGroup>
          </FilterGridRow>

          <FilterGridRow label="Time of Day">
            <ToggleButtonGroup exclusive value={local.timeOfDay} onChange={(_, val) => setLocal({ ...local, timeOfDay: val })}>
              {toggleButtons(timesOfDay)}
            </ToggleButtonGroup>
          </FilterGridRow>

          <Grid size={12} sx={{ m: 1 }} >
            <Divider />
          </Grid>

          <FilterGridRow label="Gender" indent={1}>
            <ToggleButtonGroup exclusive value={local.providerGender} onChange={(_, val) => setLocal({ ...local, providerGender: val })}>
              {toggleButtons(providerTraits.gender)}
            </ToggleButtonGroup>
          </FilterGridRow>

          <FilterGridRow label="Language" indent={1}>
            <ToggleButtonGroup exclusive value={local.providerLanguage} onChange={(_, val) => setLocal({ ...local, providerLanguage: val })}>
              {toggleButtons(providerTraits.language)}
            </ToggleButtonGroup>
          </FilterGridRow>

          {visitType.schedulingConfig.allowOverflowShifts && <FilterGridRow label="Use Overflow Shifts" indent={1}>
            <Select
              fullWidth
              displayEmpty
              value={local.useOverflowShifts ? "yes" : "no"}
              onChange={({ target: { value }}) => setLocal({ ...local, useOverflowShifts: value === "yes" })}
            >
              <MenuItem value="no">No</MenuItem>
              <MenuItem value="yes">Yes</MenuItem>
            </Select>
          </FilterGridRow>}

          <FilterGridRow label="Provider" indent={1}>
            <Select
              fullWidth
              displayEmpty
              multiple
              value={local.provider}
              onChange={({ target: { value }}) => {
                setLocal({ ...local, provider: typeof value === 'string' ? value.split(',') : value });
              }}
              renderValue={(selected) => {
                if (selected.length === 0) {
                  return <span>Any</span>;
                }
                return selected
                  .map((val) => {
                    const option = providersList.find((opt) => opt.value === val);
                    return option ? option.label : val;
                  })
                  .join(", ");
              }}
            >
              {providersList.filter(p => !p.invalidReason || p.preferred || local.provider.includes(p.value)).map((option) => (
                <MenuItem value={option.value} key={option.value}>
                  <Tooltip title={option.invalidReason}><Box sx={{ width: "100%" }}>
                    {option.label}
                    {option.invalidReason && <WarningAmberRoundedIcon
                      color="warning"
                      fontSize="small"
                      sx={{ ml: 1, verticalAlign: "text-bottom", right: 16, position: "absolute" }}
                    />}
                  </Box></Tooltip>
                </MenuItem>
              ))}
              <UnavailableProviders providers={providersList} />
            </Select>
          </FilterGridRow>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={() => { setValues(local); onClose(); }}>
          Apply
        </Button>
      </DialogActions>
    </Dialog>
  );
}

type FilterGridRowProps = PropsWithChildren & {
  label: ReactElement | string;
  indent?: number;
  loading?: boolean;
};

//TODO: SHARE
function FilterGridRow({
  label,
  loading = false,
  children,
}: FilterGridRowProps) {
  return (
    <>
      <Grid size={3} alignSelf="center">
        <Typography>{label}</Typography>
      </Grid>
      <Grid size={9}>
        {loading ? <Skeleton variant="text" sx={{ mt: 1 }} /> : children}
      </Grid>
    </>
  );
}

const providerInvalidReason = (
  p: SchedulerProvidersListQuery["scheduleProvidersList"][number],
  visitType: VisitType,
  patient: { addressState?: string | null },
  useOverflowShifts: boolean,
): string | undefined => {
  if (!p.hasServiceLine) { return "Not assigned to service line"; }
  if (!p.hasProviderType) { return "Wrong provider type"; }
  if (!p.hasStateLicense) { return `No ${patient.addressState || ""} state license`; }
  if (!p.hasInHomeShifts && !p.hasVirtualShifts) { return "No shifts"; }

  const matchingVRRs = visitType.visitResourceRequirements.filter(vrr => vrr.providerTypes.some(pt => pt.id === p.providerTypeId));
  if (matchingVRRs.every(vrr => !vrr.inHome) && !p.hasVirtualShifts) { return "No shifts for virtual visits"; }
  if (matchingVRRs.every(vrr => vrr.inHome)) {
    if (!p.hasInHomeShifts) { return "No shifts for in-home visits"; }
    if (!p.hasNearbyShifts) { return "Provider has no shifts near patient"; }
  }
  if (!useOverflowShifts && !p.hasNonOverflowShifts) { return "Provider only has overflow shifts"; }

  return undefined;
}
